import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import Header from 'shared/components/Header';
import LoadingSpinner from 'shared/components/LoadingSpinner';
import usePersistedColumnVisibility from 'shared/hooks/usePersistedColumnVisibility';
import { tokens } from 'shared/theme';
import { useAuth } from 'shared/hooks/useAuth';
import { canViewAllRecords } from 'shared/utils/canViewAllRecords';
import { buildGridToolbarExportProps } from 'shared/utils/tableExportFileName';
import { sortRowsNewestFirst } from 'shared/utils/sortRowsNewestFirst';
import useTenantsService from 'shared/api/services/tenantsService';
import CreateTenantDialog from './createTenant';
import EditTenantDialog from './editTenant';

const Tenants = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const { user } = useAuth();
  const canViewAll = canViewAllRecords(user);
  const { columnVisibilityModel, onColumnVisibilityModelChange } =
    usePersistedColumnVisibility('platform.tenants');
  const {
    tenants,
    fetchTenants,
    isLoading,
    createTenant,
    updateTenant,
    setTenantStatus,
  } = useTenantsService();

  const [createOpen, setCreateOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  const openTenantDetails = useCallback(
    (row) => {
      navigate(`/tenants/${row.id}/details`, {
        state: { tenantName: row.name },
      });
    },
    [navigate]
  );
  const handleEdit = useCallback((row) => setEditRow(row), []);
  const handleDeleteClick = useCallback((row) => setDeleteTarget(row), []);

  const confirmDelete = async () => {
    if (!deleteTarget?.id) return;
    setDeleting(true);
    try {
      await setTenantStatus(deleteTarget.id, 'DELETED');
      toast.success('Tenant marked as deleted');
      setDeleteTarget(null);
      fetchTenants();
    } catch (error) {
      const err =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        'Failed to update tenant';
      toast.error(typeof err === 'string' ? err : 'Failed to update tenant');
    } finally {
      setDeleting(false);
    }
  };

  const rows = useMemo(
    () => {
      const mapped = tenants.map((t) => ({
        id: t.id,
        name: t.name ?? '',
        url: t.url ?? '',
        username: t.username ?? '',
        driverClassName: t.driverClassName ?? '',
        initialize: Boolean(t.initialize),
        status:
          typeof t.status === 'string'
            ? t.status
            : t.status?.status ?? String(t.status ?? ''),
        primaryColor: t.primaryColor ?? '',
        secondaryColor: t.secondaryColor ?? '',
        backgroundColor: t.backgroundColor ?? '',
        tenantPhoto: t.tenantPhoto ?? '',
      }));
      const visible =
        canViewAll && showAll
          ? mapped
          : mapped.filter((r) => String(r.status).toUpperCase() !== 'DELETED');
      return sortRowsNewestFirst(visible);
    },
    [canViewAll, tenants, showAll]
  );

  const columns = useMemo(
    () => [
      { field: 'name', headerName: 'Name', flex: 1, minWidth: 140 },
      { field: 'url', headerName: 'JDBC URL', flex: 2, minWidth: 220 },
      { field: 'username', headerName: 'Username', flex: 0.8, minWidth: 100 },
      {
        field: 'driverClassName',
        headerName: 'Driver',
        flex: 1.2,
        minWidth: 180,
      },
      {
        field: 'initialize',
        headerName: 'Initialize',
        width: 110,
        type: 'boolean',
      },
      { field: 'status', headerName: 'Status', width: 120 },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 150,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <Box display="flex" alignItems="center" gap={0.25}>
            <Tooltip title="Tenant details">
              <IconButton
                size="small"
                color="success"
                onClick={() => openTenantDetails(row)}
                aria-label="Open tenant details"
              >
                <VisibilityOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                color="primary"
                onClick={() => handleEdit(row)}
                aria-label="Edit tenant"
              >
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Mark as deleted">
              <IconButton
                size="small"
                color="error"
                onClick={() => handleDeleteClick(row)}
                aria-label="Delete tenant"
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    [openTenantDetails, handleEdit, handleDeleteClick]
  );

  return (
    <Box m="20px">
      <CreateTenantDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSuccess={() => fetchTenants()}
        createTenant={createTenant}
        updateTenant={updateTenant}
      />
      <EditTenantDialog
        open={Boolean(editRow)}
        onClose={() => setEditRow(null)}
        onSuccess={() => fetchTenants()}
        tenant={editRow}
        updateTenant={updateTenant}
      />
      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => !deleting && setDeleteTarget(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete tenant?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">Do you want to delete the tenant?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmDelete}
            disabled={deleting}
          >
            {deleting ? 'Deleting…' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      <Header title="Tenants" subtitle="Central registry (all tenants)" />
      <Box mt={2} mb={2} display="flex" justifyContent="flex-end" alignItems="center" flexWrap="wrap" gap={1}>
        <Box display="flex" gap={1} flexWrap="wrap">
          {canViewAll ? (
          <Button
            variant={showAll ? 'contained' : 'outlined'}
            color="secondary"
            onClick={() => setShowAll((v) => !v)}
            sx={{ fontSize: '13px', py: 0.75, px: 1.5 }}
          >
            {showAll ? 'Active only' : 'View all'}
          </Button>
          ) : null}
          <Button
            variant="contained"
            color="secondary"
            onClick={() => setCreateOpen(true)}
            sx={{ fontSize: '13px', py: 0.75, px: 1.5 }}
          >
            Create Tenant
          </Button>
        </Box>
      </Box>
      <Box mt={0} position="relative">
        {isLoading && (
          <Box
            position="absolute"
            zIndex={2}
            left="50%"
            top="40%"
            sx={{ transform: 'translate(-50%, -50%)' }}
          >
            <LoadingSpinner />
          </Box>
        )}
        <Box
          height="72vh"
          sx={{
            opacity: isLoading ? 0.4 : 1,
            pointerEvents: isLoading ? 'none' : 'auto',
            '& .MuiDataGrid-toolbarContainer .MuiButton-text': {
              color: `${colors.grey[100]} !important`,
            },
          }}
        >
          <DataGrid
            disableRowSelectionOnClick
            rows={rows}
            columns={columns}
            pageSizeOptions={[10, 25, 50]}
            initialState={{
              pagination: { paginationModel: { pageSize: 10 } },
            }}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={onColumnVisibilityModelChange}
            slots={{ toolbar: GridToolbar }}
            slotProps={{
              toolbar: buildGridToolbarExportProps({
                title: 'Tenants',
                tableId: 'platform.tenants',
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 500 },
              }),
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default Tenants;
